import type { IAddressComponentViewModel, ILocationViewModel } from "Services/API/API";

/**
 * Google Places API (New) returns a country specific set of address components
 * (https://developers.google.com/maps/documentation/places/web-service/place-types),
 * so every logical field is resolved through an ordered list of candidate component
 * types: the first type present in the response wins.
 *
 * Supporting another country should be a single entry in COUNTRY_RULES, never a new
 * branch in the resolution code below.
 */
interface AddressComponentRules {
   city: string[];
   state: string[];
   route: string[];
   streetNumber: string[];
   postalCode: string[];
}

const ADMINISTRATIVE_AREA_LEVEL_1 = "administrative_area_level_1";

const DEFAULT_RULES: AddressComponentRules = {
   city: [
      "locality",                    // most countries
      "postal_town",                 // GB, SE, DK
      "sublocality_level_1",         // JP, BR, PH, and US boroughs
      "sublocality",
      "administrative_area_level_3", // IT comune, US New England towns
      "administrative_area_level_2", // FR, BR municipality
      "neighborhood"
   ],
   state: [ADMINISTRATIVE_AREA_LEVEL_1],
   route: ["route", "premise", "subpremise"], // JP, KR, CN frequently have no route
   streetNumber: ["street_number", "premise"],
   postalCode: ["postal_code"]
};

/* Differences from DEFAULT_RULES */
const COUNTRY_RULES: Record<string, Partial<AddressComponentRules>> = {
   // administrative_area_level_2 is a county in the US and must never become the city.
   US: { city: ["locality", "sublocality_level_1", "administrative_area_level_3", "neighborhood"] }
};

export interface PlaceAddressFormat {
   /** Any of these can be absent: which components Google returns depends on the country. */
   country?: IAddressComponentViewModel;
   city?: IAddressComponentViewModel;
   state?: IAddressComponentViewModel;
   route?: IAddressComponentViewModel;
   street_number?: IAddressComponentViewModel;
   zip?: IAddressComponentViewModel;
}

/** addressComponents is typed as always present, but the payload omits it for some places. */
const getAddressComponents = (location: ILocationViewModel | null | undefined): IAddressComponentViewModel[] =>
   (location?.addressComponents ?? []).filter(component => !!component && Array.isArray(component.types));

const hasText = (component: IAddressComponentViewModel): boolean =>
   !!(component.longText?.trim() || component.shortText?.trim());

/** Component type to the first component carrying it; Places orders them most specific first. */
const indexByType = (location: ILocationViewModel | null | undefined): Map<string, IAddressComponentViewModel> => {
   const index = new Map<string, IAddressComponentViewModel>();
   for (const component of getAddressComponents(location)) {
      if (!hasText(component))
         continue;
      for (const type of component.types) {
         if (type && !index.has(type))
            index.set(type, component);
      }
   }
   return index;
};

const pick = (index: Map<string, IAddressComponentViewModel>, types: readonly string[]): IAddressComponentViewModel | undefined => {
   for (const type of types) {
      const component = index.get(type);
      if (component)
         return component;
   }
   return undefined;
};

const isHigherCityTypeCountry = (countryCode: string): boolean => {
   if (!countryCode)
      return false;

   const countries = typeof LiquidSpaceConfig !== "undefined" ? LiquidSpaceConfig.CountriesWithHigherCityType ?? [] : [];

   return countries.some(country => country?.toUpperCase() === countryCode);
};

export const parsePlaceToAddress = (location: ILocationViewModel): PlaceAddressFormat => {
   const index = indexByType(location);

   const country = index.get("country");
   const countryCode = (country?.shortText?.trim() ?? "").toUpperCase();
   const rules: AddressComponentRules = { ...DEFAULT_RULES, ...COUNTRY_RULES[countryCode] };

   // Last resort, and only where the setting allows it: the region doubles as the city
   // (city states such as Singapore or Hong Kong). An actual city name always wins.
   const city = pick(index, rules.city)
      ?? (isHigherCityTypeCountry(countryCode) ? index.get(ADMINISTRATIVE_AREA_LEVEL_1) : undefined);

   return {
      country,
      city,
      state: pick(index, rules.state),
      route: pick(index, rules.route),
      street_number: pick(index, rules.streetNumber),
      zip: pick(index, rules.postalCode)
   };
};
